/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/anchor-is-valid */
import EditIcon from '@mui/icons-material/Edit';
import MoneyOutlinedIcon from '@mui/icons-material/MoneyOutlined';
import { Box, Skeleton } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IS_SA } from '../../../../../config/appConfig';
import { SystemSettingsService } from '../../../../../services/systemSettingsService';
import { TransferService } from '../../../../../services/transferService';
import { SectionWithHeading } from '../../../../../shared/components/common/sectionWithHeading';
import { ButtonWrapper } from '../../../../../shared/components/formUtilities/useButton';
import { InputWrapper } from '../../../../../shared/components/formUtilities/useInput';
import { TextWrapper } from '../../../../../shared/components/formUtilities/useTextWrapper';
import { transferSchema } from '../../../../../shared/validations/schema';

import { AppConstants } from '../../../../../config/appConstants';
import { OTPTypes } from '../../../../../constants/otpTypes';
import { UserDocumentTypes } from '../../../../../constants/userDocuments';
import { CountryCheckHelper } from '../../../../../helpers/countryCheckHelper';
import { FormHelper } from '../../../../../helpers/formHelper';
import { Observables } from '../../../../../observables';
import { UserService } from '../../../../../services/userService';
import { InvestorBankDetails } from '../../../../../shared/components/investorBankDetails';
import { OTPAuthenticationDialog } from '../../../../../shared/components/otpAuthenticationDialog';
import { UploadIBANDocument } from '../../../../../shared/components/uploadIbanDocumentDialog';
import { InvestorTransferHistory } from '../history';
import { WithdrawErrorDialog } from './withdrawErrorDialog';
import { WithdrawSuccess } from './withdrawSuccess';
import { WithdrawWarningDialog } from './withdrawWarningDialog';
import { getUser } from '../../../../../services/authService';

const Operations = {
	Withdraw: 1
};

export const InvestorWithdraw = () => {
	const systemSettingsService = new SystemSettingsService();
	const transferService = new TransferService();
	const userService = new UserService();
	const getTransactions = useRef(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [loading, setLoading] = useState(false);
	const [showOTPAuth, setShowOTPAuth] = useState({
		value: false,
		operation: Operations.Withdraw,
	});
	const [searchParams, setSearchParams] = useSearchParams();
	const [transferId, setTransferId] = useState(null);
	const [allDocuments, setAllDocuments] = useState([]);
	const [bankDetails, setBankDetails] = useState({});
	const [isShowIbanDialog, setIsShowIbanDialog] = useState(false);
	const [showWithdrawWarning, setShowWithdrawWarning] = useState(false);
	const [withdrawError, setWithdrawError] = useState(null);
	const [editBankDetails, setEditBankDetails] = useState(false);
	const [isAllowShowPopup, setIsAllowShowPopup] = useState(false);

	const [isNotification, setIsNotification] = useState(
		searchParams.get('isNotificationTransaction')
	);

	const navigate = useNavigate();

	const { t } = useTranslation();

	const form = useFormik({
		initialValues: { amount: searchParams.get('amount') || '' },
		validationSchema: transferSchema,
		onSubmit: (value) => {
			if (value) {
				if (isNonUAE()) {
					setShowWithdrawWarning(true);
				} else {
					checkWithdrawalProcess();
				}
			}
		},
		...FormHelper.formikProps(),
	});

	useEffect(() => {
		setIsNotification(searchParams.get('isNotificationTransaction'));
		getAllDocuments();
		getBankDetails();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (isNotification === 'true') {
			setTimeout(() => {
				document
					.getElementById('isNotification')
					.scrollIntoView({ behavior: 'smooth' });
			}, [600]);
		}
	}, [isNotification]);

	const getAllDocuments = () => {
		userService
			.getAllDocuments()
			.then((resp) => {
				setAllDocuments(resp.data);
			})
			.catch((err) => { });
	};

	const isNonUAE = () => {
		let id = bankDetails?.bankCountry?.id;
		if (id && !IS_SA) {
			return id !== AppConstants.UnitedArabEmirates;
		}
		return false;
	};

	const getBankDetails = () => {
		setLoading(true);
		transferService
			.getUserBankDetails()
			.then((resp) => {
				setBankDetails(resp.data);
				setLoading(false);
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const checkIbanDocument = () => {
		return allDocuments.find(
			(resp) => resp.type === UserDocumentTypes.InvestorIban
		)?.file?.url;
	};

	const settings = () => {
		return systemSettingsService.systemSettings();
	};

	const checkWithdrawalProcess = () => {
		const isAustralia = CountryCheckHelper.ifAustralia(bankDetails?.bankCountry);
		const requireIbanDoc = settings()?.verifyInvestorIbanDocumentUploaded && !checkIbanDocument();
		if (requireIbanDoc && !isAustralia) {
			setIsShowIbanDialog(true);
		} else {
			onWithdrawAmount();
		}
	};

	const onWarningClose = (state) => {
		setShowWithdrawWarning(false);
		if (state) {
			checkWithdrawalProcess();
		}
	};

	const onWithdrawAmount = () => {
		setIsLoading(true);
		transferService
			.addTransferOut(form.values.amount)
			.then((resp) => {
				setIsLoading(false);
				if (resp.data?.otpRequested) {
					setTransferId(resp?.data?.correlationId || null);
					setTimeout(() => {
						setShowOTPAuth({ value: true, operation: Operations.Withdraw });
					});
				} else {
					onSuccessWithdrawal();
				}
			})
			.catch((err) => {
				let errorData = err?.response?.data || {};
				if (Object.hasOwn(errorData, "IbanIsNullOrEmpty")
					|| Object.hasOwn(errorData, "BankNameIsNullOrEmpty")
					|| Object.hasOwn(errorData, "insufficientFunds")) {
					setWithdrawError(errorData);
				}
				setIsLoading(false);
			});
	};

	const onSuccessWithdrawal = () => {
		setIsSuccess(true);
		Observables.investorSummaryRefresh.next(1);
		getTransactions?.current();
	};


	const onCloseOTPAuth = (e) => {
		let operation = showOTPAuth.operation;
		setShowOTPAuth({ value: false });
		if (e) {
			if (operation === Operations.Withdraw) {
				onSuccessWithdrawal();
			}
		}
	};

	const onSetPopupValue = (transactions = []) => {
		let hasWithdraw = true;
		if (transactions && transactions?.length) {
			hasWithdraw = !!transactions.find(resp => !resp.isDeposit);
			if (!hasWithdraw) {
				setIsAllowShowPopup(true);
			}
		} else if (getUser()?.isApproved && !transactions?.length) {
			setIsAllowShowPopup(true);
		} else {
			setIsAllowShowPopup(false);
		}
	};


	const getLoader = () => {
		return (
			<Skeleton
				variant="text"
				sx={{ fontSize: '10px', width: '100px' }}
			/>
		);
	};

	return (
		<Box className="flex flex-col space-y-4">
			<Box className="grid md:grid-cols-2 grid-cols-1 gap-4">
				<SectionWithHeading
					bodyClass="md:!min-h-[164px]"
					heading={
						<Box className="flex items-center">
							<span className="mx-2">
								{!isSuccess
									? t('withdrawNotification')
									: t('withdrawRequestReceived')}
							</span>
						</Box>
					}
					children={
						<Box>
							{isSuccess ? (
								<WithdrawSuccess
									isAllowShowPopup={isAllowShowPopup}
									onDone={() => {
										form.resetForm();
										setIsSuccess(false);
									}}
									amount={form.values.amount}></WithdrawSuccess>
							) : (
								<>
									<TextWrapper
										classList="caption !text-sm"
										text={`${t('pleaseEnterTransferAmount')} ${t(
											'minimizeBankCharges'
										)}`}></TextWrapper>
									<FormikProvider value={form}>
										<Box className="mt-3 flex flex-0 md:w-[380px]">
											<InputWrapper
												autoFocus
												isCurrencyText
												label={`${t('amount')}`}
												key={'amount'}
												name="amount"></InputWrapper>
											<ButtonWrapper
												onClick={() => {
													form.getFieldHelpers('amount').setTouched(true);
													form.submitForm();
												}}
												disabled={editBankDetails}
												loading={isLoading}
												icon={<MoneyOutlinedIcon></MoneyOutlinedIcon>}
												classList="!min-w-[130px] !mt-[22px] !mx-3"
												label={t('withdraw')}></ButtonWrapper>
										</Box>
									</FormikProvider>
								</>
							)}
						</Box>
					}></SectionWithHeading>
				<SectionWithHeading
					classList={isSuccess ? '!h-auto' : ''}
					heading={
						<Box className="flex items-center">
							<span className="mx-2">{t('yourBankDetails')}</span>
						</Box>
					}
					children={
						<Box className="grid grid-flow-row gap-3">
							<Box className="grid md:grid-cols-3 grid-cols-2 items-center">
								<TextWrapper
									classList="!caption !text-sm"
									text={t('IBAN')}></TextWrapper>
								<TextWrapper
									classList="!font-bold !text-sm md:!col-span-2"
									text={
										!loading ? bankDetails?.iban : getLoader()
									}></TextWrapper>
							</Box>
							<Box className="grid md:grid-cols-3 grid-cols-2 items-center">
								<TextWrapper
									classList="!caption !text-sm"
									text={t('bankName')}></TextWrapper>
								<TextWrapper
									classList="!font-bold !text-sm md:!col-span-2"
									text={
										!loading ? bankDetails?.bankName : getLoader()
									}></TextWrapper>
							</Box>
							<Box className="grid md:grid-cols-3 grid-cols-2 items-center">
								<TextWrapper
									classList="!caption !text-sm !capitalize"
									text={t('accountName')}></TextWrapper>
								<TextWrapper
									classList="!font-bold !text-sm md:!col-span-2"
									text={
										!loading ? bankDetails?.accountName : getLoader()
									}></TextWrapper>
							</Box>
							<Box
								className={`w-full flex md:justify-end justify-center md:mt-0 mt-2 items-center ${editBankDetails ? 'opacity-0 pointer-events-none' : ''
									}`}>
								<ButtonWrapper
									onClick={() => {
										setEditBankDetails(true);
									}}
									variant={'outlined'}
									icon={
										<EditIcon
											color="primary"
											className="!text-[18px] !mx-0.5"></EditIcon>
									}
									label={t('editBankDeatils')}></ButtonWrapper>
							</Box>
						</Box>
					}></SectionWithHeading>
			</Box>
			{editBankDetails ? (
				<InvestorBankDetails isWithdraw={true} onSuccess={(resp) => {
					getBankDetails();
					setEditBankDetails(false);
				}} onCancel={() => {
					setEditBankDetails(false);
				}} />
			) : null}
			<Box id="isNotification">
				<InvestorTransferHistory onInitTransaction={(transactions) => {
					onSetPopupValue(transactions);
				}} getTransactions={getTransactions} />
			</Box>

			{showOTPAuth.value ? (
				<OTPAuthenticationDialog
					type={OTPTypes.Transaction}
					state={showOTPAuth.value}
					corrolationId={transferId}
					onClose={(e) => {
						onCloseOTPAuth(e);
					}}></OTPAuthenticationDialog>
			) : null}
			{isShowIbanDialog ? (
				<UploadIBANDocument
					state={isShowIbanDialog}
					onClose={(e) => {
						setIsShowIbanDialog(false);
						if (e) {
							onWithdrawAmount();
						}
					}}></UploadIBANDocument>
			) : null}
			{showWithdrawWarning ? (
				<WithdrawWarningDialog
					state={showWithdrawWarning}
					onClose={(e) => {
						onWarningClose(e);
					}}
				/>
			) : null}

			{withdrawError ? (
				<WithdrawErrorDialog
					error={withdrawError}
					state={!!withdrawError}
					onClose={(e) => {
						setWithdrawError(null);
					}}
				/>
			) : null}
		</Box>
	);
};
